import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class XtStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toLowerCase()}_${quote.toLowerCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.depth = depth;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL('https://sapi.xt.com/v4/public/symbol');
      const params = new URLSearchParams({
        symbol: this.pair,
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{
        result: {
          symbols: { pricePrecision: number }[];
        };
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      let prec = 1;

      if (res.data) {
        prec = res.data.result.symbols[0].pricePrecision;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('XT ticker error: ', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const url = new URL('https://sapi.xt.com/v4/public/depth');
      const params = new URLSearchParams({
        symbol: this.pair,
        limit: this.depth.toString(),
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{
        result: { asks: [string, string][]; bids: [string, string][] };
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      if (res.data) {
        const asks = res.data.result.asks;
        const bids = res.data.result.bids;

        this.asks = asks;
        this.bids = bids;

        const orderbook = {
          bids: bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
          asks: asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        };

        this.onOrderbookUpdate(orderbook);

        return orderbook;
      }
    } catch (error) {
      console.log('XT orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.xt];

    try {
      const url = new URL('https://sapi.xt.com/v4/public/kline');
      const params = new URLSearchParams({
        symbol: `${base.toLowerCase()}_${quote.toLowerCase()}`,
        interval: innerInterval,
        limit: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        result: {
          t: number;
          o: string;
          c: string;
          h: string;
          l: string;
          v: string;
        }[];
      }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      return (result.data.result ?? []).map(el => [el.t, el.o, el.h, el.l, el.c, el.v]) as KLine[];
    } catch (error) {
      console.log('XT klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://sapi.xt.com/v4/public/trade/history');
      const params = new URLSearchParams({
        symbol: `${base.toLowerCase()}_${quote.toLowerCase()}`,
        direction: 'NEXT',
        limit: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        result?: {
          items: {
            p: string;
            q: string;
            t: string;
            b: boolean;
          }[];
        };
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      return (result.data.result?.items ?? []).map(item => ({
        price: item.p,
        quantity: item.q,
        timestamp: item.t,
        side: item.b ? 'sell' : 'buy',
      }));
    } catch (error) {
      console.log('XT trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    const intervalChannel = {
      60000: '1m',
      300000: '5m',
      900000: '15m',
      3600000: '1h',
      14400000: '4h',
      86400000: '1d',
      604800000: '1w',
    }[interval];

    const ws = new ReconnectingWebSocket('wss://stream.xt.com/public', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    const id = Date.now().toString();
    const tickerChannel = `ticker@${this.pair}`;
    const klineChannel = `kline@${this.pair},${intervalChannel}`;
    const depthChannel = `depth_update@${this.pair}`;
    const tradeChannel = `trade@${this.pair}`;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send('ping');
      }
    }, 40 * 1000);

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          method: 'subscribe',
          params: [tickerChannel, klineChannel, depthChannel, tradeChannel],
          id,
        }),
      );
    };

    ws.onmessage = event => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);

        if (data.event === tickerChannel) {
          const { cr, v, q, c, h, l } = data.data;

          this.onTickerUpdate({
            baseVolume: v,
            change: cr,
            close: c,
            high: h,
            last: Number(c).toFixed(this.precision),
            low: l,
            quoteVolume: q,
          });
        }

        if (data.event === klineChannel) {
          const { t, o, c, h, l, v } = data.data;

          this.klineUpdate([Math.floor(t / 1000), o, h, l, c, v]);
        }

        if (data.event === depthChannel) {
          const responseAsks = (data.data.a ?? []) as [string, string][];
          const responseBids = (data.data.b ?? []) as [string, string][];

          const asks = mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids = mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          this.asks = asks;
          this.bids = bids;

          this.onOrderbookUpdate(
            aggregateOrderbook(
              asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.selectedPrecision,
            ),
          );
        }

        if (data.event === tradeChannel) {
          const { p, q, t, b } = data.data;

          this.onTradeUpdate({
            price: p,
            quantity: q,
            timestamp: t,
            side: b ? 'sell' : 'buy',
          });
        }
      } catch (error) {
        console.log('XT socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
      ),
    );
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new XtStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
